import { useRef, useState } from 'react';

import { useAddressSearch } from '@/hooks/useAddressSearch.ts';
import type { AddressSuggestion } from '@shared/types/entities';
import { Loader2, MapPin, X } from 'lucide-react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Rechercher une adresse…',
  className = '',
}: AddressAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { suggestions, isLoading, search, clear } = useAddressSearch();

  const handleInput = (q: string) => {
    onChange(q);
    search(q);
    setOpen(true);
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    onChange(suggestion.label);
    onSelect(suggestion);
    clear();
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    clear();
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className} z-50`}>
      <div className='relative'>
        <MapPin className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none' />
        <input
          type='text'
          value={value}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className='w-full pl-10 pr-8 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors'
          autoComplete='off'
        />
        {isLoading ? (
          <Loader2 className='absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin' />
        ) : value ? (
          <button
            type='button'
            onClick={handleClear}
            className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
          >
            <X className='w-4 h-4' />
          </button>
        ) : null}
      </div>

      {open && suggestions.length > 0 && (
        <ul className='absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto'>
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type='button'
                onMouseDown={() => handleSelect(s)}
                className='w-full flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors'
              >
                <MapPin className='w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0' />
                <span className='text-sm text-slate-700 leading-tight'>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
